/**
 * Pacific-time shipping cutoff countdown for the cart drawer.
 * Ported from main:assets/cp-countdown-timer.js as <cart-ship-countdown>.
 */
class CartShipCountdown extends HTMLElement {
  static pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  /** US Pacific (PST/PDT) from local clock. */
  static getPacificTimeDate() {
    const now = new Date();
    const startDST = new Date(now.getFullYear(), 2, 8, 2, 0, 0, 0);
    startDST.setDate(8 + ((7 - startDST.getDay()) % 7));
    const endDST = new Date(now.getFullYear(), 10, 1, 2, 0, 0, 0);
    endDST.setDate(1 + ((7 - endDST.getDay()) % 7));
    const isDST = now >= startDST && now < endDST;
    const offsetHours = isDST ? -7 : -8;
    const utc = new Date(now.getTime() + now.getTimezoneOffset() * 60000);
    return new Date(utc.getTime() + offsetHours * 60 * 60000);
  }

  static formatHMS(ms) {
    if (ms < 0) ms = 0;
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    return `${CartShipCountdown.pad(h)}:${CartShipCountdown.pad(m)}:${CartShipCountdown.pad(s)}`;
  }

  static nextMondayStart(pt) {
    const target = new Date(pt);
    const day = pt.getDay();
    const daysToMonday = (1 - day + 7) % 7 || 7;
    target.setDate(target.getDate() + daysToMonday);
    target.setHours(0, 0, 0, 0);
    return target;
  }

  constructor() {
    super();
    this._intervalId = null;
    this._messageEl = null;
  }

  _messageClassName() {
    return this.dataset.context === 'cart-page'
      ? 'mini-cart__ship-countdown-message text--strong'
      : 'mini-cart__ship-countdown-message alert alert--zero alert--center text--strong';
  }

  connectedCallback() {
    this._messageEl = this.querySelector('.mini-cart__ship-countdown-message');
    if (!this._messageEl) {
      this._messageEl = document.createElement('p');
      this._messageEl.className = this._messageClassName();
      this.appendChild(this._messageEl);
    }
    this._tick();
    clearInterval(this._intervalId);
    this._intervalId = setInterval(() => this._tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this._intervalId);
    this._intervalId = null;
  }

  _setTimedMessage(before, time, after) {
    this._messageEl.replaceChildren();
    this._messageEl.append(document.createTextNode(before));
    const timeEl = document.createElement('span');
    timeEl.className = 'mini-cart__ship-countdown-time';
    timeEl.textContent = time;
    this._messageEl.append(timeEl);
    this._messageEl.append(document.createTextNode(after));
  }

  _setPlainMessage(text) {
    this._messageEl.textContent = text;
  }

  _tick() {
    const nowPT = CartShipCountdown.getPacificTimeDate();
    const day = nowPT.getDay();
    const noonToday = new Date(nowPT);
    noonToday.setHours(12, 0, 0, 0);

    if (day === 6 || day === 0) {
      const target = CartShipCountdown.nextMondayStart(nowPT);
      this._setTimedMessage(
        'Order before ',
        CartShipCountdown.formatHMS(target - nowPT),
        ' to ship out on Monday!'
      );
      return;
    }

    if (day === 5) {
      if (nowPT < noonToday) {
        this._setTimedMessage(
          'Order in ',
          CartShipCountdown.formatHMS(noonToday - nowPT),
          ' for your order to ship today!'
        );
      } else {
        const target = CartShipCountdown.nextMondayStart(nowPT);
        this._setTimedMessage(
          'Order before ',
          CartShipCountdown.formatHMS(target - nowPT),
          ' to ship out on Monday!'
        );
      }
      return;
    }

    if (nowPT < noonToday) {
      this._setTimedMessage(
        'Order in ',
        CartShipCountdown.formatHMS(noonToday - nowPT),
        ' for your order to ship today!'
      );
    } else {
      this._setPlainMessage('Order before midnight to ship out tomorrow!');
    }
  }
}

if (!window.customElements.get('cart-ship-countdown')) {
  window.customElements.define('cart-ship-countdown', CartShipCountdown);
}
